import * as React from 'react';
import { KpiSourcePill } from '@/components/ui/kpi-source-pill';
import { cn } from '@/lib/utils/cn';

/**
 * Engagement-overview 6-column facts strip (T-0097 / E10-F03).
 *
 * Visual contract — see README §7 ASCII diagram + the `.eng-facts` rule
 * in `gaps.css`:
 *
 *   ┌────┬────┬────┬────┬────┬────┐  ← grid-template-columns: repeat(6, 1fr)
 *   │ Go │ Pha│ Pa │ Bud│ Op │ AI │
 *   └────┴────┴────┴────┴────┴────┘  ← overlaps the hero by 8px (margin-top: -8px)
 *
 * Each "fact" is a labelled KPI block:
 *   - 10px uppercase ink-500 label
 *   - 18px / 600 ink-900 value (tabular-nums)
 *   - 11px ink-500 meta line
 *   - `<KpiSourcePill>` source citation per the IV&V trust contract
 *     (README §6 — "every metric cites its origin").
 *
 * The strip:
 *   - overlaps the hero by 8px (margin-top: -8px, z-2)
 *   - rounded-bottom corners (matches `.eng-facts { border-radius: 0 0 var(--r-large) var(--r-large) }`)
 *   - 1px ink-100 border + soft navy box-shadow
 *
 * The last column carries an `ai` variant — soft lavender bg
 * (`var(--ai-surface)`) — for the AI-cost KPI per the prototype.
 *
 * Translation note: the prototype uses inline styles per-fact for the
 * "AI proposals" column tint; we expose `variant: 'ai'` so the
 * component, not the consumer, owns the AI-surface lavender. Aligns
 * with CLAUDE.md §3 ("Load-bearing UI primitives ... built once in
 * apps/web/src/components/ui/ and reused").
 */
export interface FactItem {
  /** Short uppercase label (e.g. "Go-Live"). */
  label: string;
  /** Headline value. Strings allow custom formatting (currency, %, etc.). */
  value: React.ReactNode;
  /** Optional sub-label rendered under the value at 11px ink-500. */
  meta?: React.ReactNode;
  /**
   * Source attribution (e.g. "Scorecard · Snapshot 2026-05-20"). Passes
   * to `<KpiSourcePill>` per README §6 ("every metric cites its origin").
   */
  source: string;
  /** Switch to AI-surface lavender treatment (used by the AI-cost cell). */
  variant?: 'default' | 'ai';
  /**
   * Optional control rendered at the right edge of the label row (e.g. an
   * edit pencil for tiles whose value the user can change). Keep it icon-
   * sized — the cell is ~1/6 of the strip.
   */
  action?: React.ReactNode;
}

export interface FactsStripProps {
  /** Exactly six items per the hero pattern; we don't hard-enforce so a
   *  consumer can render fewer during graceful-degradation states. */
  items: FactItem[];
  className?: string;
}

/**
 * One column per fact. The IV&V overview has the prototype's six; a
 * Discovery and Selection overview has five or six, and the strip must not
 * leave empty cells, so the column count follows the items (Tailwind needs
 * the class names spelled out).
 */
const COLUMNS: Record<number, string> = {
  4: 'grid-cols-4',
  5: 'grid-cols-5',
  6: 'grid-cols-6',
};

export function FactsStrip({ items, className }: FactsStripProps) {
  return (
    <div
      className={cn(
        // Overlap the hero by 8px; relative + z so the box-shadow
        // floats above the hero edge.
        'relative z-[2] -mt-2',
        'grid overflow-hidden rounded-b-large bg-paper-0',
        COLUMNS[items.length] ?? 'grid-cols-6',
        'border border-t-0 border-ink-100',
        // Approximate the `0 6px 20px rgba(24,46,88,0.06)` rule from
        // `.eng-facts`. Tailwind's shadow utilities don't expose a
        // direct navy alpha shadow, so an arbitrary value carries it.
        'shadow-[0_6px_20px_rgb(24_46_88_/_0.06)]',
        className,
      )}
    >
      {items.map((item, idx) => (
        <FactCell key={`${item.label}-${idx}`} item={item} isLast={idx === items.length - 1} />
      ))}
    </div>
  );
}

function FactCell({ item, isLast }: { item: FactItem; isLast: boolean }) {
  const isAi = item.variant === 'ai';
  return (
    <div
      className={cn(
        'flex min-w-0 flex-col gap-[2px] px-4 py-[14px]',
        !isLast && 'border-r border-ink-100',
        isAi && 'bg-ai-surface',
      )}
    >
      <div
        className={cn(
          'flex items-center justify-between gap-1 text-[10px] font-semibold uppercase tracking-[0.06em]',
          isAi ? 'text-ai-ink' : 'text-ink-500',
        )}
      >
        <span className="truncate">{item.label}</span>
        {item.action ?? null}
      </div>
      <div
        className={cn(
          'text-[18px] font-semibold leading-[1.2] tracking-[-0.005em] tabular-nums',
          isAi ? 'text-ai-ink' : 'text-ink-900',
        )}
      >
        {item.value}
      </div>
      {item.meta ? (
        <div className={cn('text-[11px] leading-[1.3]', isAi ? 'text-ai-ink/80' : 'text-ink-500')}>
          {item.meta}
        </div>
      ) : null}
      <KpiSourcePill source={item.source} className="mt-1.5" />
    </div>
  );
}
